import {keepPreviousData, useInfiniteQuery} from '@tanstack/react-query';
import {loadData} from '../helper/data-loader';
import JSONParser from '@core/scripts/helper/json-parser';
import {Constants} from '../view-constants';
import {buildQueryKeyUrl, rebuildUrl} from '@article-overview/components/hooks/rebuild-url.js';
import {useEffect, useRef} from 'react';

export const useInfiniteArticles = ({
    enabled,
    initialAjaxUrl,
    ajaxUrl,
    givenSeoOptimizedUrl,
    givenSanitizedUrl,
    onContentUpdated,
    maxPages,
    initialModel,
}) => {
    const initialUrlRef = useRef(initialAjaxUrl);

    const removeArticlesFromModel = (model) => {
        if (!model) return model;
        const modelCopy = {...model};
        if (modelCopy.articleContent) {
            modelCopy.articleContent = {...modelCopy.articleContent};
            modelCopy.articleContent.articleListPageItems = [];
        }
        return modelCopy;
    };

    const mapModelToOject = (modelJson, pageParam) => {
        const articles = modelJson.articleContent?.articleListPageItems || [];

        return {
            articles,
            page: pageParam,
            filterContent: modelJson.filterContent,
            navigationContent: modelJson.navigationContent,
            actualPageSize: modelJson.articleContent?.actualPageSize,
            shopContent: modelJson.shopContent,
            seoOptimizedUrl: modelJson.seoOptimizedUrl ?? givenSeoOptimizedUrl,
            sanitizedUrl: modelJson.sanitizedUrl ?? givenSanitizedUrl,
            hits: modelJson.hits,
            configurationContent: modelJson.configurationContent,
            model: removeArticlesFromModel(modelJson),
        };
    };
    const extractQueryData = (result, pageParam) => {
        const modelJson = JSONParser.tryParseJSON(result.model);
        return mapModelToOject(modelJson, pageParam);
    };

    const finalAjaxUrl = ajaxUrl || initialAjaxUrl;

    const getPageParam = (urlString) => {
        if (!urlString) return 1;
        const url = new URL(urlString, window.location.origin);
        return parseInt(url.searchParams.get(Constants.SEARCH_PARAMS_PAGE)) || 1;
    };

    const initialPage = getPageParam(finalAjaxUrl)


    const queryKeyAjaxUrl = buildQueryKeyUrl(finalAjaxUrl, true);
    const cleanedInitialUrl = rebuildUrl(initialUrlRef.current, getPageParam(initialUrlRef.current));

    const queryKey = ['articles', 'infinite', queryKeyAjaxUrl];
    const config = {
        queryKey,
        queryFn: async ({pageParam = 1}) => {

            const cleanedAjaxUrl = rebuildUrl(finalAjaxUrl,pageParam);

            if ( cleanedAjaxUrl === cleanedInitialUrl && enabled) {

                const initialData = mapModelToOject(initialModel, pageParam);
                if (initialData) {
                    return initialData;
                }
            }
            const urlWithPage = rebuildUrl(finalAjaxUrl, pageParam);

            const result = await loadData(urlWithPage);
            if (result) {
                return extractQueryData(result, pageParam);
            }

            return {
                articles: [],
                page: pageParam - 1,
            };
        },
        getNextPageParam: (lastPage) => {
            if (!lastPage) return undefined;
            const currentPage = lastPage.page;
            const calculatedMaxPages = lastPage.hits ? Math.ceil(lastPage.hits / Constants.PAGE_SIZE) : maxPages;
            const effectiveMaxPages = calculatedMaxPages || maxPages;
            if (effectiveMaxPages && currentPage < effectiveMaxPages) {
                return currentPage + 1;
            }
            return undefined;
        },
        getPreviousPageParam: (firstPage) => {
            if (!firstPage) return undefined;
            const currentPage = firstPage.page;
            if (currentPage > 1) {
                return currentPage - 1;
            }
            return undefined;
        },
        initialPageParam: initialPage,
        enabled: enabled,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
        placeholderData: keepPreviousData
    };

    const query = useInfiniteQuery(config);

    const prevModelRef = useRef();

    useEffect(() => {
        prevModelRef.current = undefined;
    }, [queryKeyAjaxUrl]);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (query.data && onContentUpdated) {
            const model = query.data.pages.find((page) => page.model)?.model;

            if (model && model !== prevModelRef.current) {

                prevModelRef.current = model;
                onContentUpdated({model});
            }
        }
    }, [query.data, enabled]);
    return query;
};
